import { FC, useEffect, useState } from "react";
import IconButton from "../../../common/icon-button/icon-button";
import TextOverflowTooltip from "../../../common/TextOverflowTooltip";
import WidgetOptions from "../widget-options/widget-options";
import CustomInput from "./ip-subnet-calculator-custom-input/CustomInput";

import copyIcon from "../../../../assets/images/copy-btn.svg";
import unfoldIcon from "../../../../assets/images/unfold-btn.svg";
import foldIcon from "../../../../assets/images/fold-btn.svg";
import { IIpSubnetCalculatorProps } from "./models/ip-subnet-calculator.props"
import { DEFAULT_SUBNET_VALUES, MASK_BITS, MAX_ADDRESSES, MAX_SUBNETS, SUBNET_MASKS, SUBNET_MASKS_MAPPING } from "./constants"
import { IIpSubnetCalculatorState } from "./models/ip-subnet-calculator.state"
import { getBaseInfo, isIpAddressValid, getWildcardMask, getHexIp, getBitmap, getIpSubnetConstsByMaskBits } from './util'
import { steveDataStorage } from "../../../../helpers/sdk-helpers";
import { withResizeDetector } from "react-resize-detector";
import { error, info } from "../../../../helpers/notifications";
import "./ip-subnet-calculator.scss";

const IpSubnetCalculator: FC<IIpSubnetCalculatorProps> = ({
  isEditAllowed,
  showWidgetInfo,
  onRemove,
  onClone,
  data,
  width,
}) => {
  const [isExpended, setIsExpended] = useState(false);
  const [ipAddressInputValue, setIpAddressInputValue] = useState('10.0.0.1');

  const [ipSubnetCalculatorData, setIpSubnetCalculatorData] = useState<IIpSubnetCalculatorState>(DEFAULT_SUBNET_VALUES);


  useEffect(() => {
    const ipSubnetCalculatorData: { ipAddress: string, maskBits: number } = steveDataStorage('ipSubnetCalculator', 'formData', false).get(data.id);
    if (!ipSubnetCalculatorData) {
      return;
    }

    const ipSubnetConst = getIpSubnetConstsByMaskBits(ipSubnetCalculatorData.maskBits);
    setIpSubnetCalculatorData((data) => ({
      ...data,
      ipSubnetConst,
      ipAddress: ipSubnetCalculatorData.ipAddress
    }));

  }, [data.id]);

  useEffect(() => {
    const saveData = () => {
      if (!isIpAddressValid(ipSubnetCalculatorData.ipAddress) || (!ipSubnetCalculatorData.ipSubnetConst.maskBits && ipSubnetCalculatorData.ipSubnetConst.maskBits !== 0)) {
        return;
      }
      steveDataStorage('ipSubnetCalculator', 'formData', false).set({ ipAddress: ipSubnetCalculatorData.ipAddress, maskBits: ipSubnetCalculatorData.ipSubnetConst.maskBits }, data.id);
    }

    const recalculateAll = () => {
      if (!isIpAddressValid(ipSubnetCalculatorData.ipAddress)) {
        setIpSubnetCalculatorData(DEFAULT_SUBNET_VALUES);
        return;
      }
      const baseInfo = getBaseInfo(ipSubnetCalculatorData.ipAddress, ipSubnetCalculatorData.ipSubnetConst.maskBits);
      const subnetBitmap = getBitmap(ipSubnetCalculatorData.ipSubnetConst.maskBits);
      if (!subnetBitmap) return;

      const wildcardMask = getWildcardMask(ipSubnetCalculatorData.ipSubnetConst.subnetMask);
      const hexIp = getHexIp(ipSubnetCalculatorData.ipAddress);

      setIpSubnetCalculatorData((data) => ({
        ...data,
        broadCastAddress: baseInfo?.broadCastAddress,
        hostAddressRange: baseInfo?.hostAddressRange,
        subnetId: baseInfo?.subnetId,
        subnetBitmap,
        wildcardMask,
        hexIp,
      }));

      saveData();
    }
    setIpAddressInputValue(ipSubnetCalculatorData.ipAddress);
    recalculateAll();
  }, [ipSubnetCalculatorData.ipAddress, ipSubnetCalculatorData.ipSubnetConst, data.id]);

  const onSubnetMaskChange = (value: string) => {
    const newMaskBits = Number(Object.keys(SUBNET_MASKS_MAPPING).find(key => SUBNET_MASKS_MAPPING[Number(key)] === value));
    const ipSubnetConst = getIpSubnetConstsByMaskBits(newMaskBits);

    setIpSubnetCalculatorData((data) => ({
      ...data,
      ipSubnetConst
    }));
  }

  const onMaskBitsChange = (value: string) => {
    const ipSubnetConst = getIpSubnetConstsByMaskBits(Number(value));

    setIpSubnetCalculatorData((data) => ({
      ...data,
      ipSubnetConst
    }));
  }

  const onMaxSubnetsChange = (value: string) => {
    const newMaskBits = Math.round(Math.log(Number(value)) / Math.log(2));
    const ipSubnetConst = getIpSubnetConstsByMaskBits(newMaskBits);

    setIpSubnetCalculatorData((data) => ({
      ...data,
      ipSubnetConst
    }));
  }

  const onMaxAddressesChange = (value: string) => {
    const newMaskBits = 32 - Math.round((Math.log(Number(value) + 2) / Math.log(2)));
    const ipSubnetConst = getIpSubnetConstsByMaskBits(newMaskBits);

    setIpSubnetCalculatorData((data) => ({
      ...data,
      ipSubnetConst
    }));
  }

  const onSubnetBitsChange = (value: string) => {
    const ipSubnetConst = getIpSubnetConstsByMaskBits(Number(value));
    setIpSubnetCalculatorData((data) => ({
      ...data,
      ipSubnetConst
    }));
  }

  const onCopyTextToClipboard = async () => {
    const newClip = generateTextForCopy();

    try {
      await navigator.clipboard.writeText(newClip);
      info({
        message: "Results copied to clipboard."
      })
    } catch (err) {
      error({
        message: "An error occurred while copying the results."
      })
    }
  }


  const changeIpAddress = (value: string) => {
    setIpSubnetCalculatorData((data) => ({
      ...data,
      ipAddress: value
    }));
  }

  const setIpAddress = (value: string) => {
    setIpAddressInputValue(value);
  }

  const toggleExpend = () => {
    setIsExpended(!isExpended);
  };

  const generateTextForCopy = () => {
    return `Wildcard Mask
      ${ipSubnetCalculatorData.wildcardMask}
      Host Address Range
      ${ipSubnetCalculatorData.hostAddressRange}
      Subnet ID
      ${ipSubnetCalculatorData.subnetId}
      Broadcast Address
      ${ipSubnetCalculatorData.broadCastAddress}
      Subnet Bitmap
      ${ipSubnetCalculatorData.subnetBitmap}
      First Octet Range
      0 - 255
      Hex IP Address
      ${ipSubnetCalculatorData.hexIp}
    `.replace(/^ +/gm, '')
  }

  return (
    <div className="glu-dashboard-card glu-content">
      <div className="glu-content__header">
        {isEditAllowed && (
          <div className="glu-flex-shrink">
            <IconButton
              className="glu-icon__draggable cursor-move"
              icon="drag"
            />
          </div>
        )}
        <TextOverflowTooltip
          title={data.title}
          renderChildren={(ref: string) => (
            <div className="glu-flex-grow">
              <div className="glu-page__title glu-widget-title" ref={ref}>
                {data.title}
              </div>
            </div>
          )}
        />

        {isEditAllowed && (
          <WidgetOptions
            showWidgetInfo={showWidgetInfo(data)}
            onRemove={onRemove(data)}
            onClone={onClone(data)}
          />
        )}
      </div>
      <div className="glu-content__body">
        <div className="glu-content__section glu-content__separator glu-content__separator--inner glu-spread-v glu-overflow-auto glu-flex-col ip-subnet-parent-container">
          {!isExpended && (
            <div className={width <= 255 ? "ipCalculatorContainerSmall" : "ipCalculatorContainer"}>
              <CustomInput testId="ip-address-input" label="IP Address" type="string" value={ipAddressInputValue} onChange={setIpAddress} onBlur={changeIpAddress} />
              <CustomInput testId="subnet-mask-input" label="Subnet Mask" type="select" value={ipSubnetCalculatorData.ipSubnetConst.subnetMask} options={SUBNET_MASKS} onChange={onSubnetMaskChange} />
              <CustomInput testId="subnet-bits-input" value={ipSubnetCalculatorData.ipSubnetConst.subnetBits} label="Subnet Bits" type="select" options={MASK_BITS} onChange={onSubnetBitsChange} />
              <CustomInput testId="mask-bits-input" label="Mask Bits" type="select" value={ipSubnetCalculatorData.ipSubnetConst.maskBits} options={MASK_BITS} onChange={onMaskBitsChange} />
              <CustomInput testId="maximum-subnets-input" label="Maximum Subnets" type="select" options={MAX_SUBNETS} onChange={onMaxSubnetsChange} value={ipSubnetCalculatorData.ipSubnetConst.maximumSubnets} />
              <CustomInput testId="hosts-per-subnet-input" label="Hosts per Subnet" type="select" options={MAX_ADDRESSES} onChange={onMaxAddressesChange} value={ipSubnetCalculatorData.ipSubnetConst.hostsPerSubnet} />
            </div>
          )}
          <div className="config-preview-container">
            <div className="action-buttons-container">
              <img src={copyIcon} alt="copy-icon" className="copy-icon" onClick={onCopyTextToClipboard} />
              <img
                src={isExpended ? foldIcon : unfoldIcon}
                alt="unfold-icon"
                className="unfold-icon"
                onClick={toggleExpend}
              />
            </div>
            <div className="ip-calculator-result">
              <div className="ip-calculator-result-item">
                <div>Wildcard Mask</div>
                {ipSubnetCalculatorData.wildcardMask}
              </div>
              <div className="ip-calculator-result-item">
                <div>Host Address Range</div>
                {ipSubnetCalculatorData.hostAddressRange}
              </div>
              <div className="ip-calculator-result-item">
                <div>Subnet ID</div>
                {ipSubnetCalculatorData.subnetId}
              </div>
              <div className="ip-calculator-result-item">
                <div>Broadcast Address</div>
                {ipSubnetCalculatorData.broadCastAddress}
              </div>
              <div className="ip-calculator-result-item">
                <div>Subnet Bitmap</div>
                {ipSubnetCalculatorData.subnetBitmap}
              </div>
              <div className="ip-calculator-result-item">
                <div>First Octet Range</div>
                <div>0 - 255</div>
              </div>
              <div className="ip-calculator-result-item">
                <div>Hex IP Address</div>
                {ipSubnetCalculatorData.hexIp}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withResizeDetector(IpSubnetCalculator);

